/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CalculatedUserAvailabilityOut } from '../models/CalculatedUserAvailabilityOut';
import type { EnrichedEventData } from '../models/EnrichedEventData';
import type { EntityType } from '../models/EntityType';
import type { NoteData } from '../models/NoteData';
import type { OrderData } from '../models/OrderData';
import type { PrivateSlotOut } from '../models/PrivateSlotOut';
import type { ShiftOut } from '../models/ShiftOut';
import type { UnavailabilityData } from '../models/UnavailabilityData';
import type { WaitingListItemData } from '../models/WaitingListItemData';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class CalendarService {
  /**
   * Get Orders
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static getCalendarOrders(
    startDate?: string,
    endDate?: string,
  ): CancelablePromise<Record<string, Array<OrderData>>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/calendar/orders',
      query: {
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Unavailabilities
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @returns UnavailabilityData Successful Response
   * @throws ApiError
   */
  public static getCalendarUnavailabilities(
    startDate?: string,
    endDate?: string,
  ): CancelablePromise<Record<string, Array<UnavailabilityData>>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/calendar/unavailabilities',
      query: {
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Shifts
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @returns ShiftOut Successful Response
   * @throws ApiError
   */
  public static getCalendarShifts(
    startDate?: string,
    endDate?: string,
  ): CancelablePromise<Record<string, Array<ShiftOut>>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/calendar/shifts',
      query: {
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Calendar User Availability
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @returns CalculatedUserAvailabilityOut Successful Response
   * @throws ApiError
   */
  public static getCalendarUserAvailability(
    startDate?: string,
    endDate?: string,
  ): CancelablePromise<Record<string, Array<CalculatedUserAvailabilityOut>>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/calendar/user-availability',
      query: {
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Event
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @returns EnrichedEventData Successful Response
   * @throws ApiError
   */
  public static getCalendarEvents(
    startDate?: string,
    endDate?: string,
  ): CancelablePromise<Record<string, Array<EnrichedEventData>>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/calendar/events',
      query: {
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Notes
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @returns NoteData Successful Response
   * @throws ApiError
   */
  public static getCalendarNotes(
    startDate?: string,
    endDate?: string,
  ): CancelablePromise<Record<string, Array<NoteData>>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/calendar/notes',
      query: {
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Waiting List
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @returns WaitingListItemData Successful Response
   * @throws ApiError
   */
  public static getCalendarWaitingList(
    startDate?: string,
    endDate?: string,
  ): CancelablePromise<Record<string, Array<WaitingListItemData>>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/calendar/waiting-list',
      query: {
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Private Slots Availabilities
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @returns PrivateSlotOut Successful Response
   * @throws ApiError
   */
  public static getCalendarPrivateSlotsAvailability(
    startDate?: string,
    endDate?: string,
  ): CancelablePromise<Record<string, Array<PrivateSlotOut>>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/calendar/private-slots',
      query: {
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Recurrent Unavailability Count
   * @param recurrentId
   * @param entityType
   * @param fromDate Start date (YYYY-MM-DD)
   * @returns number Successful Response
   * @throws ApiError
   */
  public static getRecurrentCount(
    recurrentId: string,
    entityType: EntityType,
    fromDate?: string,
  ): CancelablePromise<number> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/calendar/recurrent-count',
      query: {
        recurrent_id: recurrentId,
        entity_type: entityType,
        from_date: fromDate,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }
}
